import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { apiGet } from "@/lib/api";
import type { EventDto, PostDto, PublicSettings } from "@/types";
import { format, parseISO } from "date-fns";
import { de } from "date-fns/locale";
import { SidebarMiniCalendar } from "@/components/calendar/SidebarMiniCalendar";

/**
 * Public-side sidebar.
 *
 * Phase 3: the contact box and the contact/terms/quicklinks icons are gone
 * (specification 2.5) - they duplicated information that is now reachable
 * from the menu and footer. Instead the sidebar leads with a 3-month
 * mini-calendar (the most useful information that is not visible
 * anywhere else nearby).
 *
 * What stays: a compact list of upcoming events (so the visitor sees the
 * next few items without having to scroll the mini-calendar) and the
 * latest posts.
 */
export function Sidebar() {
  const locale = de;

  const { data: upcoming = [] } = useQuery({
    queryKey: ["upcoming", "de"],
    queryFn: () =>
      apiGet<EventDto[]>(`/public/events/upcoming?locale=${"de"}&limit=3`),
    staleTime: 60_000,
  });
  const { data: latest } = useQuery({
    queryKey: ["latest", "de"],
    queryFn: () =>
      apiGet<{ content: PostDto[] }>(`/public/posts?locale=${"de"}&size=3`),
    staleTime: 60_000,
  });
  const { data: settings } = useQuery({
    queryKey: ["public-settings"],
    queryFn: () => apiGet<PublicSettings>("/public/settings"),
    staleTime: 10 * 60_000,
  });

  return (
    <>
      {/* 3-month calendar with category dots & filter chips. */}
      <SidebarMiniCalendar />

      <SidebarCard title={"Nächste Termine"}>
        {upcoming.length === 0 ? (
          <p className="text-sm text-text-muted italic">{"Keine Termine in diesem Zeitraum."}</p>
        ) : (
          <ul className="space-y-3">
            {upcoming.map((e) => (
              <li key={e.id}>
                <Link to={`/kalender/${e.slug}`} className="block hover:text-primary">
                  <div className="text-xs uppercase tracking-wide text-text-muted">
                    {format(parseISO(e.startAt), "d. MMM yyyy", { locale })}
                  </div>
                  <div className="text-sm font-medium">{e.title}</div>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </SidebarCard>

      <SidebarCard title={"Letzte Meldungen"}>
        {!latest || latest.content.length === 0 ? (
          <p className="text-sm text-text-muted italic">{"Keine Einträge gefunden."}</p>
        ) : (
          <ul className="space-y-3">
            {latest.content.map((p) => (
              <li key={p.id}>
                <Link to={`/aktuelles/${p.slug}`} className="block hover:text-primary">
                  <div className="text-xs uppercase tracking-wide text-text-muted">
                    {p.publishedAt
                      ? format(parseISO(p.publishedAt), "d. MMM yyyy", { locale })
                      : ""}
                  </div>
                  <div className="text-sm font-medium">{p.title}</div>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </SidebarCard>

      {/*
        Quicklinks: kept as a fallback only if admins configured them.
        The proper Schnellzugriff hub is now /services (spec 4).
      */}
      {settings?.quicklinks && settings.quicklinks.length > 0 && (
        <SidebarCard title={"Schnellzugriff"}>
          <ul className="space-y-2 text-sm">
            {settings.quicklinks.map((q) => (
              <li key={q.href}>
                <a className="text-primary hover:underline" href={q.href}>
                  {q.label}
                </a>
              </li>
            ))}
          </ul>
        </SidebarCard>
      )}
    </>
  );
}

function SidebarCard({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className="bg-bg-soft border border-border rounded-lg p-4">
      <h2 className="font-bold text-sm uppercase tracking-wide text-primary mb-3 font-sans">
        {title}
      </h2>
      {children}
    </section>
  );
}
